
import React, { useState } from "react";
import { motion } from "framer-motion";
import SectionHeading from "../SectionHeading";
import DetectiveBackground from "./gallery/DetectiveBackground";
import { useTranslation } from 'react-i18next';
import { useAllGalleryImages } from "@/hooks/useGallery";
import { Skeleton } from "../ui/skeleton";
import Masonry from "./gallery/Masonry";
import "./gallery/Masonry.css";

const GallerySection = () => {
  const { t } = useTranslation();
  const { images, loading, error } = useAllGalleryImages();
  const [hoveredImage, setHoveredImage] = useState<string | null>(null);
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.08
      }
    }
  };

  return (
    <section className="py-12 pt-20 pb-24 relative overflow-hidden">
      <DetectiveBackground />
      
      <div className="container mx-auto px-4 z-10 relative">
        <SectionHeading
          title={t('gallery.title')}
          description={t('gallery.description')}
          centered={true}
        />
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mt-10"
        >
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {[...Array(12)].map((_, i) => (
                <Skeleton key={i} className="w-full aspect-square rounded-lg" />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8 bg-black/50 rounded-lg">
              <p className="text-red-400">Error loading gallery images</p>
            </div>
          ) : images && images.length > 0 ? (
            <div className="evidence-board p-6 relative">
              {/* Evidence string connections - decorative */}
              <div className="absolute inset-0 z-0 overflow-hidden">
                <svg width="100%" height="100%" className="opacity-10">
                  <line x1="20%" y1="30%" x2="40%" y2="70%" stroke="#FFA000" strokeWidth="1" />
                  <line x1="60%" y1="20%" x2="80%" y2="40%" stroke="#FFA000" strokeWidth="1" />
                  <line x1="30%" y1="80%" x2="70%" y2="60%" stroke="#FFA000" strokeWidth="1" />
                  <line x1="10%" y1="40%" x2="50%" y2="20%" stroke="#FFA000" strokeWidth="1" />
                  <line x1="90%" y1="30%" x2="60%" y2="70%" stroke="#FFA000" strokeWidth="1" />
                </svg>
              </div>
              
              <Masonry 
                data={images} 
                hoveredImage={hoveredImage}
                onHover={(id) => setHoveredImage(id)}
                onLeave={() => setHoveredImage(null)}
              />

              {/* Magnifying glass decoration */}
              <div className="absolute -bottom-5 -right-5 hidden md:block opacity-20 pointer-events-none">
                <div className="w-24 h-24 border-4 border-white/50 rounded-full relative">
                  <div className="absolute w-8 h-16 bg-white/50 -right-4 bottom-2 rotate-45 rounded-lg"></div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-black/50 rounded-lg">
              <p className="text-white/70">No gallery images available</p>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default GallerySection;
